/**
 * AnimatedCheckbox Component
 * Custom animated circular checkbox with SVG checkmark animation
 */

import { useRef, useLayoutEffect } from 'react';
import styles from './AnimatedCheckbox.module.css';

// Global cache to track which checkboxes have been animated
// This persists across component remounts (react-window virtualization)
const animatedCheckboxCache = new Set<string>();

interface AnimatedCheckboxProps {
  checked: boolean;
  onChange: (e: React.MouseEvent) => void;
  size?: number; // Size in pixels (default 24)
  className?: string;
  id?: string; // Unique ID for tracking animation state across remounts
}

export function AnimatedCheckbox({
  checked,
  onChange,
  size = 24,
  className = '',
  id,
}: AnimatedCheckboxProps) {
  const spanRef = useRef<HTMLSpanElement>(null);

  // Scale SVG based on size (default design is 24px)
  const scale = size / 24;
  const svgWidth = Math.round(12 * scale);
  const svgHeight = Math.round(9 * scale);

  useLayoutEffect(() => {
    if (!spanRef.current) return;

    // If checked and we haven't animated this checkbox before
    if (checked && id && !animatedCheckboxCache.has(id)) {
      // Mark as animated
      animatedCheckboxCache.add(id);

      const span = spanRef.current;
      // Remove and re-add animation class to trigger it
      span.classList.remove(styles.animateBounce);
      // Force reflow
      void span.offsetWidth;
      span.classList.add(styles.animateBounce);

      // Remove animation class after it completes
      const timer = setTimeout(() => {
        span.classList.remove(styles.animateBounce);
      }, 600);

      return () => clearTimeout(timer);
    }

    // If unchecked, remove from cache so it can animate again next time
    if (!checked && id) {
      animatedCheckboxCache.delete(id);
    }
  }, [checked, id]);

  return (
    <div
      className={`${styles.checkboxWrapper} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onChange(e);
      }}
    >
      <label className={styles.cbx}>
        <span
          ref={spanRef}
          className={checked ? styles.checked : ''}
          style={{
            width: `${size}px`,
            height: `${size}px`,
          }}
        >
          <svg
            width={`${svgWidth}px`}
            height={`${svgHeight}px`}
            viewBox="0 0 12 9"
          >
            <polyline points="1 5 4 8 11 1" />
          </svg>
        </span>
      </label>
    </div>
  );
}
